import { FC, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { LeaseDetailTabs, LeaseDetailTabKey } from './LeaseDetailTabs'
import { LeaseConditionsSection } from './LeaseConditionsSection'
import { AmortizationTableSection } from './AmortizationTableSection'
import GeneralData from '../proposalReview/proposalAdjustments/GeneralData'
import VehicleData from '../proposalReview/proposalAdjustments/VehicleData'
import authService from '../../../services/authService'
import {
  AmortizationTable,
  InProgressLeaseApplication,
  ProposalGeneralInfo,
  ProposalQuoteConditions,
  ProposalVehicleData,
} from '@/types/management.types'
import { formatDateLong } from '../../../utils/date'
import { isRejectedCreditApplicationStatus } from '../../../utils/creditApplicationStatus'

// Mapea la respuesta de credit-application/in-progress (la misma que ya
// alimenta la lista de "Mis arrendamientos") a los tipos que ya consumen
// GeneralData/VehicleData — mismo criterio de campos que
// mapProposalsResponse en PendingProposalsRoute.tsx.
const mapLeaseDetailInfo = (
  lease: InProgressLeaseApplication
): { proposalGeneralInfo: ProposalGeneralInfo; vehicleData: ProposalVehicleData } => {
  const cot = lease.cotizacion
  const requestData = cot.request_data

  return {
    proposalGeneralInfo: {
      fecha: formatDateLong(cot.created_at),
      nombreCliente: cot.sellersCustomer?.company || '',
      atencionA: cot.sellersCustomer?.contact || '',
      tipoDeActivo: requestData.tipo_activo || '',
      numeroCotizaciones: 1,
    },
    vehicleData: {
      marca: requestData.brand,
      modelo: requestData.year,
      submarcaVersion: requestData.subBrand || '',
      tipo: requestData.tipo || '',
      valorFactura: String(requestData.valor_factura || ''),
      costoAccesorios: String(requestData.accesorios || ''),
      descripcionAccesorios: requestData.descripcion_accesorios || '',
      periodoCotizacion: String(requestData.plazo_meses || ''),
    },
  }
}

// Arma las condiciones ya calculadas (en pesos) del resumen de la
// cotización final — regresa undefined si el backend todavía no manda
// finalCotizacion/resumen (créditos previos a este cambio, o cotización
// aún no seleccionada).
const mapLeaseConditions = (
  lease: InProgressLeaseApplication
): ProposalQuoteConditions | undefined => {
  const resumen = lease.finalCotizacion?.resumen?.[0]
  if (!resumen) return undefined

  return {
    leaseAmount: String(resumen.monto_arrendamiento_siva ?? ''),
    insurance: String(resumen.seguro ?? ''),
    totalInitialAmount: String(resumen.pago_inicial_total ?? ''),
    openingFee: String(resumen.comision_apertura_siva ?? ''),
    residualValueNoTax: String(resumen.residual_siva ?? ''),
    monthlyRentNoTax: String(resumen.renta_mensual_calculada ?? ''),
    monthlyDiscount: String(resumen.descuento_mensual ?? ''),
    totalRentWithDiscount: String(resumen.renta_mensual_descuento ?? ''),
    rentTax: String(resumen.iva_renta_mensual_descuento ?? ''),
    reserveFund: String(resumen.fondo_reserva ?? ''),
    totalRentWithTax: String(resumen.total_renta_mensual_descuento ?? ''),
  }
}

export const LeaseDetailContent: FC = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<LeaseDetailTabKey>('generales')
  const [lease, setLease] = useState<InProgressLeaseApplication | null>(
    (location.state as { lease?: InProgressLeaseApplication } | null)
      ?.lease || null
  )
  const [isLoading, setIsLoading] = useState(!lease)
  const [amortization, setAmortization] = useState<AmortizationTable | null>(
    null
  )
  const [amortizationLeaseId, setAmortizationLeaseId] = useState<
    string | null
  >(null)
  const [isLoadingAmortization, setIsLoadingAmortization] = useState(false)

  useEffect(() => {
    // Si ya llegó por state (click en "Ver detalles"), no hay nada que
    // pedir. Si no (entrada directa por URL o refresh), se busca en la
    // misma lista de arrendamientos en proceso — no existe endpoint de
    // detalle por id.
    if (lease || !id) return

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const inProgress = await authService.getInProgressCreditApplications()
        let found = inProgress.find(item => item.id === id) || null
        if (!found) {
          const rejected = await authService.getRejectedCreditApplications()
          found = rejected.find(item => item.id === id) || null
        }
        if (!cancelled) {
          setLease(found)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [id, lease])

  useEffect(() => {
    // Solo se pide la tabla de amortización cuando esa tab está activa, y
    // solo una vez por arrendamiento (se cachea en amortizationLeaseId).
    if (activeTab !== 'amortizacion' || !lease) return
    if (amortizationLeaseId === lease.id) return

    let cancelled = false
    const load = async () => {
      setIsLoadingAmortization(true)
      try {
        const data = await authService.getAmortizationTable(lease.id)
        if (!cancelled) {
          setAmortization(data)
          setAmortizationLeaseId(lease.id)
        }
      } finally {
        if (!cancelled) setIsLoadingAmortization(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [activeTab, lease, amortizationLeaseId])

  const { proposalGeneralInfo, vehicleData } = lease
    ? mapLeaseDetailInfo(lease)
    : { proposalGeneralInfo: undefined, vehicleData: undefined }

  const isRejected = isRejectedCreditApplicationStatus(lease?.status)

  return (
    <>
      <button
        type="button"
        onClick={() => navigate('/mis-arrendamientos')}
        className="flex items-center gap-1.5 border border-gray-300 rounded-full px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-olr-grey-1 transition-colors mb-4"
      >
        <ChevronLeft size={16} />
        Regresar
      </button>

      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Detalle de arrendamiento
      </h2>

      <LeaseDetailTabs
        active={activeTab}
        onChange={setActiveTab}
        onlyGenerales={isRejected}
      />

      {isLoading ? (
        <div className="bg-white border border-olr-grey-2 rounded-lg p-12 text-center text-olr-body">
          Cargando...
        </div>
      ) : !lease ? (
        <div className="bg-white border border-olr-grey-2 rounded-lg p-12 text-center text-olr-body">
          No se encontró el arrendamiento.
        </div>
      ) : activeTab === 'generales' ? (
        <>
          <GeneralData generalInfo={proposalGeneralInfo} />
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
            Cotización
          </h3>
          <VehicleData generalInfo={vehicleData} />
        </>
      ) : activeTab === 'condiciones' ? (
        <LeaseConditionsSection conditions={mapLeaseConditions(lease)} />
      ) : activeTab === 'amortizacion' ? (
        <AmortizationTableSection
          table={amortization ?? undefined}
          isLoading={isLoadingAmortization}
        />
      ) : (
        <div className="bg-white border border-olr-grey-2 rounded-lg p-12 text-center text-olr-body">
          Próximamente
        </div>
      )}
    </>
  )
}

export default LeaseDetailContent
